import React, { useState, useContext } from 'react';
import classNames from 'classnames';
import { QuoteModel } from '@/types';
import QuoteSvg from '@/assets/quote.svg';
import { isColorDark, lightenDarkenColor } from '@/utils';
import { generateHashCode } from '@/utils/scrapbookUtils';
import { saveToScrapbook } from '@/api/scrapbook-data';
import { Tooltip, useNotification, SystemFeaturesContext, UserContext, MetadataContext } from '@/components';
import FolderSelectionDialog from '@/components/scrapbook/dialogs/FolderSelectionDialog';
import ScissorIcon from '@/assets/scissors-light.svg';
import Copy from '@/assets/copy-simple-light.svg';

type QuoteType = {
  quote: QuoteModel;
  backgroundColor?: string;
  hideButtons?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const Quote = ({ quote, backgroundColor, hideButtons, className, ...props }: QuoteType) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [saveObject, setSaveObject] = useState<any>(null);
  const { showNotification } = useNotification();
  const { saveToScrapbook: saveToScrapbookFeature } = useContext(SystemFeaturesContext);
  const user = useContext(UserContext);
  const metadata = useContext(MetadataContext);

  if (!quote) {
    return null;
  }

  let quoteSvgColor = null;
  if (backgroundColor === '#e6eeec') {
    quoteSvgColor = '#cfdedb';
  } else if (backgroundColor === '#ece3d6') {
    quoteSvgColor = '#dacab3';
  } else if (backgroundColor === '#f3e5dc') {
    quoteSvgColor = '#e8cebf';
  } else {
    const _backgroundColor = backgroundColor || '#000000';
    if (isColorDark(_backgroundColor)) {
      // if there is no backgroundColor lighten the #000 to get a grey else lighten by 20%
      quoteSvgColor = lightenDarkenColor(_backgroundColor, !backgroundColor ? 180 : 20);
    }
    // this is light background and lighten the quote sign by 20%
    else {
      quoteSvgColor = lightenDarkenColor(_backgroundColor, 20);
    }
  }

  const handleSaveToScrapbook = () => {
    if (!quote) return;

    const content = {
      pageId: metadata?.pageId,
      pageTitle: metadata?.pageTitle,
      pageUrl: metadata?.pageUrl,
      preTitle: quote.preTitle,
      quote: quote.quote,
      expert: quote.expert,
      backgroundColor,
    };

    const linkObject = {
      path: `quote.${metadata?.pageId || 'unknown'}.${generateHashCode(quote.quote)}.json`,
      contentType: 'application/x.stylus.quote+json',
      content: JSON.stringify(content),
    };

    setSaveObject(linkObject);
    setShowFolderDialog(true);
  };

  const handleCopyToClipboard = async () => {
    if (!quote) return;

    const formattedHtmlContent = `<html><p>${quote.preTitle}</p><p>${quote.quote}</p><p>${quote.expert?.expertName || ''}</p><p>${quote.expert?.jobTitle || ''}</p></html>`;
    const formattedContent = `${quote.preTitle}\t\n${quote.quote}\t\n${quote.expert?.expertName || ''}\t\n${quote.expert?.jobTitle || ''}\n\n`;

    try {
      if (navigator.clipboard && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([formattedHtmlContent], { type: 'text/html' }),
            'text/plain': new Blob([formattedContent], { type: 'text/plain' }),
          }),
        ]);
        showNotification('Copied to clipboard', 'savesuccess');
      }
    } catch {
      showNotification('Failed to copy to clipboard', 'error');
    }
  };

  return (
    <div
      className={classNames('color-from-bg mb-10 w-full sm:mb-0', className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      <div className="quote-container relative flex w-full">
        <QuoteSvg className="absolute top-0 left-0 h-[116px] w-[116px]" style={{ fill: quoteSvgColor }} />
        <QuoteSvg
          className="absolute right-0 -bottom-13 h-[116px] w-[116px] rotate-180 sm:right-13 sm:-bottom-11"
          style={{ fill: quoteSvgColor }}
        />
        <div className="flex w-full flex-col gap-4 px-1 pt-16 pb-6 sm:px-5 sm:pt-6 sm:pb-6 md:px-5 lg:px-15 xl:px-23">
          <div
            className="relative text-lg leading-[22px] font-bold"
            dangerouslySetInnerHTML={{ __html: quote.preTitle }}
          />
          <p
            className={classNames('relative text-[26px] leading-8', metadata.isPublic ? 'italic' : 'font-soehne-wide')}
            dangerouslySetInnerHTML={{ __html: quote.quote }}
          />
          {!hideButtons && saveToScrapbookFeature && isHovered && (
            <div className="absolute top-[22px] right-[-8px] flex gap-1">
              <Tooltip placement="bottom" content="Save to Scrapbook">
                <button className="btn icon cursor-pointer rounded-full bg-white p-2" onClick={handleSaveToScrapbook}>
                  <ScissorIcon className="h-7 w-7" />
                </button>
              </Tooltip>
              <Tooltip placement="top" content="Copy to Clipboard">
                <button className="btn icon cursor-pointer rounded-full bg-white p-2" onClick={handleCopyToClipboard}>
                  <Copy className="h-7 w-7" />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      <FolderSelectionDialog
        isOpen={showFolderDialog}
        onClose={() => setShowFolderDialog(false)}
        onSave={async (folderId: number) => {
          try {
            const result = await saveToScrapbook(folderId, saveObject, user?.userId);
            if (result?.succeeded) {
              showNotification(result.message || 'Quote saved to scrapbook successfully', 'savesuccess');
            } else {
              showNotification('Call to scrapbook server failed.', 'error');
            }
          } catch {
            showNotification('Call to scrapbook server failed.', 'error');
          }
        }}
        reportTitle="Save Quote to Scrapbook"
      />
    </div>
  );
};

export default Quote;
